import React, { useState, useEffect, useCallback } from 'react'
import {
  CCard, CCardBody, CButton, CCol, CRow,
  CModal, CModalHeader, CModalBody,
  CFormGroup, CLabel, CInput,
} from 'compat/coreui'
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_APP_API_URL;
const authHeaders = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

const badge = (deal) => {
  if (String(deal.discountType) === '2' && deal.discount) return `${deal.discount}% OFF`;
  if (String(deal.discountType) === '3') return 'BOGO';
  if (deal.discount) return `$${deal.discount}`;
  return 'DEAL';
};

export default function Deals() {
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dealsData, setDealsData] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', type: 'Online', discountType: '2', discount: '' });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const getDeals = useCallback(() => {
    axios.get(`${API}/api/deal`, { headers: authHeaders() })
      .then(response => { if (response.data.status) setDealsData(response.data.deals || []); })
      .catch(() => {})
  }, []);

  useEffect(() => { getDeals() }, [getDeals]);

  const addDeal = (e) => {
    e.preventDefault();
    if (!form.title || !form.description) { toast.warn('Title and description are required'); return; }
    if (form.discountType !== '3' && !form.discount) { toast.warn('Set the discount amount'); return; }
    setSaving(true);
    axios.post(`${API}/api/deal`, {
      title: form.title,
      description: form.description,
      category: '',
      type: form.type,
      discount: form.discount,
      discountType: parseInt(form.discountType, 10),
    }, { headers: authHeaders() }).then(response => {
      if (response.data.status) {
        setModal(false);
        setForm({ title: '', description: '', type: 'Online', discountType: '2', discount: '' });
        toast.success('Deal is live on your shop page');
        getDeals();
      } else {
        toast.error(response.data.message || 'Could not add the deal');
      }
    }).catch(() => toast.error('Could not add the deal')).finally(() => setSaving(false));
  };

  return (
    <>
      <CRow className="mb-3">
        <CCol>
          <h2 style={{ fontWeight: 800, marginBottom: 4 }}>Deals & Specials</h2>
          <p className="text-muted">Offers appear on your shop page and in the public deals feed — free on every plan.</p>
        </CCol>
        <CCol style={{ textAlign: 'right' }}>
          <CButton color="success" className="text-white" style={{ padding: '11px 26px' }} onClick={() => setModal(true)}>
            + New Deal
          </CButton>
        </CCol>
      </CRow>

      {dealsData.length === 0 ? (
        <CCard>
          <CCardBody className="text-center" style={{ padding: '54px 20px' }}>
            <span style={{ fontSize: 40 }} aria-hidden="true">🏷️</span>
            <h4 style={{ fontWeight: 800, margin: '12px 0 6px' }}>No deals yet</h4>
            <p className="text-muted" style={{ maxWidth: 420, margin: '0 auto 18px' }}>
              Shops with active deals get featured placement in the deals feed. Post your first offer in under a minute.
            </p>
            <CButton color="success" className="text-white" onClick={() => setModal(true)}>Create your first deal</CButton>
          </CCardBody>
        </CCard>
      ) : (
        <div className="bp-deals-grid">
          {dealsData.map((deal, i) => (
            <article className="bp-deal-card" key={deal._id || i}>
              <div className="bp-deal-top">
                <div className="bp-deal-fallback" aria-hidden="true">🏷️</div>
                <span className="bp-deal-badge">{badge(deal)}</span>
              </div>
              <div className="bp-deal-body">
                <h4>{deal.title}</h4>
                {deal.description && <p>{deal.description}</p>}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {deal.type && <span className="bp-shop-chip">{deal.type}</span>}
                  {deal.createdAt && <small className="text-muted">{moment(deal.createdAt).format('MMM D, YYYY')}</small>}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <CModal show={modal} onClose={() => setModal(false)}>
        <CModalHeader closeButton>New Deal</CModalHeader>
        <CModalBody>
          <form onSubmit={addDeal}>
            <CFormGroup>
              <CLabel>Deal title *</CLabel>
              <CInput placeholder="e.g. 20% off all earbuds this week" value={form.title} onChange={set('title')} />
            </CFormGroup>
            <CFormGroup>
              <CLabel>Description *</CLabel>
              <CInput placeholder="What's the offer and how do shoppers get it?" value={form.description} onChange={set('description')} />
            </CFormGroup>
            <CFormGroup className="row px-3">
              <CCol md={6} className="pl-0">
                <CLabel>Deal type</CLabel>
                <select className="custom-select product_status" value={form.discountType} onChange={set('discountType')}>
                  <option value="2">Percentage off</option>
                  <option value="1">Fixed price</option>
                  <option value="3">Buy one get one</option>
                </select>
              </CCol>
              <CCol md={6} className="pr-0">
                <CLabel>{form.discountType === '1' ? 'Price ($)' : form.discountType === '2' ? 'Discount (%)' : 'Value (optional)'}</CLabel>
                <CInput type="number" min="0" placeholder={form.discountType === '2' ? 'e.g. 20' : 'e.g. 10'} value={form.discount} onChange={set('discount')} />
              </CCol>
            </CFormGroup>
            <CFormGroup>
              <CLabel>Where does it apply?</CLabel>
              <select className="custom-select product_status" value={form.type} onChange={set('type')}>
                <option>Online</option>
                <option>In-store</option>
                <option>Both</option>
              </select>
            </CFormGroup>
            <CButton color="success" type="submit" className="text-white" disabled={saving}>
              {saving ? 'Publishing…' : 'Publish Deal'}
            </CButton>{' '}
            <CButton color="secondary" onClick={() => setModal(false)}>Cancel</CButton>
          </form>
        </CModalBody>
      </CModal>
    </>
  )
}
